import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChildType, SingleChildType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Child } from 'service/dto/children.dto';

export const useCreateChildren = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (children: ChildType): Promise<void> => {
      return services.childrenService.createChildren(children);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-children']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateChildren = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      childrenId,
      children
    }: {
      childrenId: number | null;
      children: SingleChildType;
    }): Promise<void> => {
      return services.childrenService.updateChildren(childrenId, children);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-children']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetChildrenList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['childrens'],
    () => services.childrenService.getChildrens(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteChildren = () =>
  useMutation(
    (childId: number) => services.childrenService.deleteChildren(childId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetChildrenDetails = ({ childId }: { childId: number }) => {
  return useQuery<Child, AxiosError<Error>>(
    ['get_child', childId],
    () => services.childrenService.getChildren(childId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!childId
    }
  );
};
